import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';

export type WaterIntelViewportRequest = {
  south: number;
  west: number;
  north: number;
  east: number;
  rows?: number;
  cols?: number;
};

export type WaterIntelViewportRead = {
  schemaVersion: 1;
  dataset: 'GEBCO_2026';
  bounds: { south: number; west: number; north: number; east: number };
  rows: number;
  cols: number;
  depthsMeters: Array<number | null>;
  minDepthMeters: number | null;
  maxDepthMeters: number | null;
  waterCellCount: number;
  generatedAtMs: number;
  quality: {
    confidence: number;
    label: 'regional_model';
    evidenceKind: 'interpolated';
    horizontalResolutionM: number;
  };
};

const cache = new Map<string, { expiresAtMs: number; value: WaterIntelViewportRead }>();
const inflight = new Map<string, Promise<WaterIntelViewportRead | null>>();
const CACHE_MS = 5 * 60 * 1000;

function round(value: number, digits = 3) {
  const p = 10 ** digits;
  return Math.round(value * p) / p;
}

function cacheKey(input: WaterIntelViewportRequest) {
  return [
    round(input.south),
    round(input.west),
    round(input.north),
    round(input.east),
    input.rows || 32,
    input.cols || 32,
  ].join(':');
}

export async function getWaterIntelViewport(
  input: WaterIntelViewportRequest,
): Promise<WaterIntelViewportRead | null> {
  const key = cacheKey(input);
  const hit = cache.get(key);
  if (hit && hit.expiresAtMs > Date.now()) return hit.value;
  if (inflight.has(key)) return inflight.get(key)!;

  const task = (async () => {
    try {
      const user = getAuth().currentUser;
      if (!user?.uid) return null;
      await user.getIdToken(false);

      const callable = httpsCallable<WaterIntelViewportRequest, WaterIntelViewportRead>(
        functions,
        'getWaterIntelViewport',
      );
      const result = await callable(input);
      const value = result?.data;
      if (!value || value.schemaVersion !== 1 || !Array.isArray(value.depthsMeters)) {
        return null;
      }

      cache.set(key, { expiresAtMs: Date.now() + CACHE_MS, value });
      return value;
    } catch (error) {
      console.warn('[waterIntel][viewport] soft fail', error);
      return null;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, task);
  return task;
}
